import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { chatService, ChatMessage } from '../../services/chatService';
import { MessageBubble } from './MessageBubble';
import { SuggestedQuestions } from './SuggestedQuestions';
import { QuestionsBrowser } from './QuestionsBrowser';
import { useAuth } from '../../context/AuthContext';
import { chatLimitService } from '../../services/chatLimitService';
import { favoritesApiService } from '../../services/favoritesApi';
import { statisticsService, UserStatistics } from '../../services/statisticsService';
import { translationService, Language } from '../../services/translationService';
import { chatNotificationService } from '../../services/chatNotificationService';
import { chatHistoryApiService } from '../../services/chatHistoryApi';

interface ChatWindowProps {
  isMinimized: boolean;
  onClose: () => void;
  onMinimize: () => void;
  onMaximize: () => void;
}

const STORAGE_KEY = 'islamic_chat_history';

export const ChatWindow: React.FC<ChatWindowProps> = ({
  isMinimized,
  onClose,
  onMinimize,
  onMaximize,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);
  const [messageHistory, setMessageHistory] = useState<string[]>([]);
  const [remainingRequests, setRemainingRequests] = useState<number>(5);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'chat' | 'favorites' | 'history' | 'stats'>('chat');
  const [searchQuery, setSearchQuery] = useState('');
  const [isPlayingAudio, setIsPlayingAudio] = useState<string | null>(null);
  const synthRef = useRef<SpeechSynthesis | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<Language>('fr');
  const [statistics, setStatistics] = useState<UserStatistics | null>(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [favorites, setFavorites] = useState<any[]>([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [quickSuggestions, setQuickSuggestions] = useState<Array<{ label: string; action: string }>>([]);
  const [showQuestionsBrowser, setShowQuestionsBrowser] = useState(false);
  const [lastUserQuestion, setLastUserQuestion] = useState<string>('');
  const [displayedWelcomeText, setDisplayedWelcomeText] = useState<string>('');
  const [isTypingWelcome, setIsTypingWelcome] = useState<boolean>(false);
  const [typingMessages, setTypingMessages] = useState<Record<string, string>>({});
  const [isTypingResponse, setIsTypingResponse] = useState<Record<string, boolean>>({});
  const [userPoints, setUserPoints] = useState<number>(0);
  const [userBadges, setUserBadges] = useState<string[]>([]);
  const [showConfetti, setShowConfetti] = useState(false);

  // Initialiser le service de synthèse vocale
  useEffect(() => {
    if ('speechSynthesis' in window) {
      synthRef.current = window.speechSynthesis;
    }
  }, []);

  // Charger la langue préférée
  useEffect(() => {
    const lang = translationService.getLanguage();
    setCurrentLanguage(lang);
  }, []);

  // Charger les favoris si connecté (au chargement et quand on change d'onglet)
  useEffect(() => {
    if (user && activeTab === 'favorites') {
      loadFavorites();
    }
  }, [user, activeTab]);

  // Charger les favoris aussi au montage si l'utilisateur est connecté (pour mettre à jour le compteur)
  useEffect(() => {
    if (user) {
      loadFavorites();
    }
  }, [user]);

  // Charger les statistiques si connecté
  useEffect(() => {
    if (user && activeTab === 'stats') {
      loadStatistics();
    }
  }, [user, activeTab]);

  const loadFavorites = async () => {
    if (!user) return;
    setLoadingFavorites(true);
    try {
      const favs = await favoritesApiService.getAllFavorites();
      setFavorites(favs);
    } catch (error) {
      console.error('Erreur lors du chargement des favoris:', error);
    } finally {
      setLoadingFavorites(false);
    }
  };

  const loadStatistics = async () => {
    if (!user) return;
    try {
      const stats = await statisticsService.getStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      // En cas d'erreur, définir des stats vides
      setStatistics({
        totalQuestions: 0,
        totalInteractions: 0,
        favoriteCategories: [],
        mostUsedFeatures: [],
        averageResponseTime: 0,
        totalFavorites: 0,
        questionsByDay: [],
      });
    }
  };

  // Vérifier les notifications
  useEffect(() => {
    setNotificationsEnabled(chatNotificationService.isEnabled());
  }, []);

  // Charger les suggestions rapides depuis la base de connaissances
  useEffect(() => {
    const loadSuggestions = async () => {
      try {
        const suggestions = await chatService.getQuickSuggestions();
        setQuickSuggestions(suggestions);
      } catch (error) {
        console.error('Erreur lors du chargement des suggestions:', error);
        // Fallback vers des suggestions de base
        setQuickSuggestions([
          { label: '🕌 Horaires de prière', action: 'Horaires de prière' },
          { label: '📖 Verset du jour', action: 'Verset aléatoire' },
          { label: '📜 Hadith', action: 'Donne-moi un hadith' },
          { label: '🤲 Doua', action: 'Donne-moi une doua' },
          { label: '🕌 Nom d\'Allah', action: 'Nom d\'Allah' },
          { label: '❓ Comment prier ?', action: 'Comment faire la prière ?' },
          { label: '📚 5 Piliers', action: 'Quels sont les cinq piliers de l\'Islam ?' },
        ]);
      }
    };
    loadSuggestions();
  }, []);


  // Raccourcis clavier
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Esc pour fermer
      if (e.key === 'Escape' && !isMinimized) {
        onClose();
      }
      // Ctrl+F pour rechercher
      if ((e.ctrlKey || e.metaKey) && e.key === 'f' && !isMinimized) {
        e.preventDefault();
        if (activeTab === 'chat' || activeTab === 'history') {
          const searchInput = document.querySelector('input[placeholder*="Rechercher"]') as HTMLInputElement;
          searchInput?.focus();
        }
      }
    };

    if (!isMinimized) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isMinimized, activeTab, onClose]);

  // Vérifier les requêtes restantes
  useEffect(() => {
    const checkLimit = () => {
      const limit = chatLimitService.getRemainingRequests(!!user);
      setRemainingRequests(limit);
    };
    checkLimit();
    // Vérifier toutes les 5 secondes
    const interval = setInterval(checkLimit, 5000);
    return () => clearInterval(interval);
  }, [user]);

  // Charger l'historique depuis l'API ou localStorage
  useEffect(() => {
    const loadHistory = async () => {
      if (user) {
        // Si connecté, charger depuis l'API (charger plus d'interactions pour avoir l'historique complet)
        try {
          const data = await chatHistoryApiService.getAllInteractions({ limit: 200 }); // Augmenter à 200 pour avoir plus d'historique
          if (data.results && data.results.length > 0) {
            // Convertir les interactions en messages
            const historyMessages: ChatMessage[] = [];
            const apiMessageIds = new Set<string>(); // Pour éviter les doublons
            
            data.results.forEach((interaction) => {
              // Valider que timestamp existe et est valide
              let timestamp: Date;
              // Le backend Django retourne 'timestamp' comme string ISO
              if (interaction.timestamp) {
                try {
                  const ts = new Date(interaction.timestamp);
                  // Vérifier si la date est valide
                  if (!isNaN(ts.getTime())) {
                    timestamp = ts;
                  } else {
                    timestamp = new Date(); // Fallback sur la date actuelle
                  }
                } catch (error) {
                  console.warn('Date invalide dans interaction:', interaction.timestamp, error);
                  timestamp = new Date(); // Fallback sur la date actuelle
                }
              } else {
                timestamp = new Date(); // Fallback sur la date actuelle
              }
              
              const userId = `user-${interaction.id}`;
              const assistantId = `assistant-${interaction.id}`;
              
              // Message utilisateur
              historyMessages.push({
                id: userId,
                type: 'user',
                content: interaction.question,
                timestamp: timestamp,
              });
              apiMessageIds.add(userId);
              
              // Message assistant
              historyMessages.push({
                id: assistantId,
                type: 'assistant',
                content: interaction.answer,
                timestamp: timestamp,
              });
              apiMessageIds.add(assistantId);
            });
            
            // Fusionner avec localStorage pour récupérer les messages récents qui ne sont pas encore dans l'API
            const savedHistory = localStorage.getItem(STORAGE_KEY);
            if (savedHistory) {
              try {
                const parsed = JSON.parse(savedHistory);
                if (Array.isArray(parsed)) {
                  // Ajouter seulement les messages du localStorage qui ne sont pas déjà dans l'API
                  // et qui sont récents (moins de 5 minutes)
                  const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
                  parsed.forEach((msg: any) => {
                    if (msg.id !== 'welcome' && !apiMessageIds.has(msg.id)) {
                      const msgTimestamp = new Date(msg.timestamp).getTime();
                      // Si le message est récent (moins de 5 minutes) ou si on n'a pas de timestamp
                      if (!msg.timestamp || msgTimestamp > fiveMinutesAgo) {
                        historyMessages.push({
                          ...msg,
                          timestamp: new Date(msg.timestamp || Date.now()),
                        });
                      }
                    }
                  });
                }
              } catch (error) {
                console.warn('Erreur lors de la fusion avec localStorage:', error);
              }
            }
            
            // Trier par timestamp et ajouter le message de bienvenue
            historyMessages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
            const welcomeMessage: ChatMessage = {
              id: 'welcome',
              type: 'assistant',
              content: chatService.getWelcomeMessage(),
              timestamp: new Date(),
            };
            setMessages([welcomeMessage, ...historyMessages]);
            // Si on a seulement le message de bienvenue, démarrer l'animation
            if (historyMessages.length === 0) {
              setIsTypingWelcome(true);
              setDisplayedWelcomeText('');
            }
            return;
          }
        } catch (error) {
          console.error('Erreur lors du chargement de l\'historique depuis l\'API:', error);
          // Fallback vers localStorage
        }
      }
      
      // Fallback: charger depuis localStorage
      const savedHistory = localStorage.getItem(STORAGE_KEY);
      if (savedHistory) {
        try {
          const parsed = JSON.parse(savedHistory);
          if (Array.isArray(parsed)) {
            setMessages(parsed.map((msg: any) => ({
              ...msg,
              timestamp: new Date(msg.timestamp),
            })));
            return;
          }
        } catch (error) {
          console.error('Erreur lors du chargement de l\'historique:', error);
        }
      }
      
      // Message de bienvenue initial si aucun historique
      const welcomeMessage: ChatMessage = {
        id: 'welcome',
        type: 'assistant',
        content: chatService.getWelcomeMessage(),
        timestamp: new Date(),
      };
      setMessages([welcomeMessage]);
      // Démarrer l'animation typewriter pour le message de bienvenue
      setIsTypingWelcome(true);
      setDisplayedWelcomeText('');
    };

    loadHistory();
  }, [user]);

  // Arrêter l'animation si une question est posée
  useEffect(() => {
    if (messages.length > 1 || (messages.length === 1 && messages[0].id !== 'welcome')) {
      setIsTypingWelcome(false);
      setDisplayedWelcomeText(chatService.getWelcomeMessage());
    }
  }, [messages]);

  // Animation typewriter pour le message de bienvenue (se répète toutes les 3 secondes)
  useEffect(() => {
    // Ne pas animer si une question a été posée
    if (messages.length > 1 || (messages.length === 1 && messages[0].id !== 'welcome')) {
      setIsTypingWelcome(false);
      setDisplayedWelcomeText(chatService.getWelcomeMessage());
      return;
    }

    if (!isTypingWelcome) {
      // Si l'animation est terminée, attendre 3 secondes puis redémarrer
      const restartTimeout = setTimeout(() => {
        setIsTypingWelcome(true);
        setDisplayedWelcomeText('');
      }, 3000); // Attendre 3 secondes avant de redémarrer
      
      return () => clearTimeout(restartTimeout);
    }
    
    const welcomeMessage = chatService.getWelcomeMessage();
    let currentIndex = 0;
    
    const typeInterval = setInterval(() => {
      // Vérifier si une question a été posée pendant l'animation
      if (messages.length > 1 || (messages.length === 1 && messages[0].id !== 'welcome')) {
        setIsTypingWelcome(false);
        setDisplayedWelcomeText(welcomeMessage);
        clearInterval(typeInterval);
        return;
      }

      if (currentIndex < welcomeMessage.length) {
        setDisplayedWelcomeText(welcomeMessage.substring(0, currentIndex + 1));
        currentIndex++;
      } else {
        // Animation terminée, attendre 3 secondes puis redémarrer
        setIsTypingWelcome(false);
        clearInterval(typeInterval);
      }
    }, 50); // Vitesse d'affichage : 50ms par lettre
    
    return () => clearInterval(typeInterval);
  }, [isTypingWelcome, messages]);

  // Sauvegarder l'historique dans localStorage (uniquement si non connecté, ou en plus pour backup)
  useEffect(() => {
    if (messages.length > 0) {
      // Pour les utilisateurs non connectés, sauvegarder dans localStorage
      // Pour les utilisateurs connectés, garder aussi un backup dans localStorage en cas de problème API
      // Filtrer le message de bienvenue pour éviter de le sauvegarder à chaque fois
      const messagesToSave = messages.filter(msg => msg.id !== 'welcome');
      if (messagesToSave.length > 0) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
      }
    }
  }, [messages]);

  // Scroll automatique vers le bas
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus sur l'input quand la fenêtre s'ouvre
  useEffect(() => {
    if (!isMinimized) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isMinimized]);

  // Gérer la navigation dans l'historique avec les flèches
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp' && messageHistory.length > 0) {
        e.preventDefault();
        if (currentHistoryIndex === -1) {
          setCurrentHistoryIndex(messageHistory.length - 1);
        } else if (currentHistoryIndex > 0) {
          setCurrentHistoryIndex(currentHistoryIndex - 1);
        }
        setInputValue(messageHistory[currentHistoryIndex] || '');
      } else if (e.key === 'ArrowDown' && currentHistoryIndex !== -1) {
        e.preventDefault();
        if (currentHistoryIndex < messageHistory.length - 1) {
          setCurrentHistoryIndex(currentHistoryIndex + 1);
          setInputValue(messageHistory[currentHistoryIndex + 1] || '');
        } else {
          setCurrentHistoryIndex(-1);
          setInputValue('');
        }
      }
    };

    if (!isMinimized && inputRef.current === document.activeElement) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isMinimized, messageHistory, currentHistoryIndex]);

  const handleSendMessage = async (messageText?: string) => {
    const text = messageText || inputValue.trim();
    if (!text || isLoading) return;

    // Vérifier la limite pour les non connectés
    const isAuthenticated = !!user;
    const limitCheck = chatLimitService.canMakeRequest(isAuthenticated);

    if (!limitCheck.allowed) {
      setShowLimitModal(true);
      return;
    }

    // Enregistrer la requête
    chatLimitService.recordRequest(isAuthenticated);
    setRemainingRequests(limitCheck.remaining - 1);

    // Ajouter le message de l'utilisateur
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setCurrentHistoryIndex(-1);
    
    // Ajouter à l'historique des messages
    setMessageHistory((prev) => [...prev, text]);

    setIsLoading(true);

    try {
      // Construire l'historique de conversation (exclure le message de bienvenue)
      const conversationHistory = messages
        .filter(msg => msg.id !== 'welcome')
        .slice(-10) // Garder les 10 derniers messages pour le contexte
        .map(msg => ({
          role: msg.type === 'user' ? 'user' as const : 'assistant' as const,
          content: msg.content,
        }));
      
      // Obtenir la réponse du service avec contexte conversationnel
      const response = await chatService.processMessage(text, conversationHistory);

      const assistantMessageId = (Date.now() + 1).toString();
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        type: 'assistant',
        content: response,
        timestamp: new Date(),
      };

      // Ajouter le message avec contenu vide pour commencer l'animation
      setMessages((prev) => [...prev, { ...assistantMessage, content: '' }]);
      
      // Démarrer l'animation typewriter pour la réponse
      setIsTypingResponse(prev => ({ ...prev, [assistantMessageId]: true }));
      setTypingMessages(prev => ({ ...prev, [assistantMessageId]: '' }));
      
      // Animation typewriter : afficher mot par mot
      const words = response.split(' ');
      let wordIndex = 0;
      
      const typeInterval = setInterval(() => {
        if (wordIndex < words.length) {
          const currentText = words.slice(0, wordIndex + 1).join(' ');
          setTypingMessages(prev => ({ ...prev, [assistantMessageId]: currentText }));
          
          // Mettre à jour le message avec le texte progressif
          setMessages((prev) => 
            prev.map(msg => 
              msg.id === assistantMessageId 
                ? { ...msg, content: currentText }
                : msg
            )
          );
          
          wordIndex++;
        } else {
          // Animation terminée
          setIsTypingResponse(prev => {
            const newState = { ...prev };
            delete newState[assistantMessageId];
            return newState;
          });
          setTypingMessages(prev => {
            const newState = { ...prev };
            delete newState[assistantMessageId];
            return newState;
          });
          // S'assurer que le message final a le contenu complet
          setMessages((prev) => 
            prev.map(msg => 
              msg.id === assistantMessageId 
                ? { ...msg, content: response }
                : msg
            )
          );
          clearInterval(typeInterval);
          
          // Ajouter des points et vérifier les badges
          addPoints(10);
        }
      }, 30); // Vitesse : 30ms par mot
      
      // Garder la question de l'utilisateur pour afficher les questions similaires
      setLastUserQuestion(text);
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: '❌ Désolé, une erreur est survenue. Veuillez réessayer plus tard.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (action: string) => {
    handleSendMessage(action);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearHistory = async () => {
    if (window.confirm('Voulez-vous vraiment effacer l\'historique de conversation ?')) {
      try {
        // Si l'utilisateur est connecté, supprimer aussi de l'API
        if (user) {
          // TODO: Ajouter une méthode API pour supprimer l'historique si nécessaire
          // Pour l'instant, on supprime juste du localStorage
          console.log('[ChatWindow] Suppression de l\'historique pour utilisateur connecté');
        }
        
        // Supprimer du localStorage
        setMessages([]);
        setMessageHistory([]);
        localStorage.removeItem(STORAGE_KEY);
        
        // Réinitialiser avec le message de bienvenue
        const welcomeMessage: ChatMessage = {
          id: 'welcome',
          type: 'assistant',
          content: chatService.getWelcomeMessage(),
          timestamp: new Date(),
        };
        setMessages([welcomeMessage]);
        // Redémarrer l'animation typewriter
        setIsTypingWelcome(true);
        setDisplayedWelcomeText('');
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'historique:', error);
      }
    }
  };

  // Fonctions pour le partage
  const handleShare = (content: string) => {
    const textToShare = content.replace(/\*\*/g, '').substring(0, 500);
    if (navigator.share) {
      navigator.share({
        title: 'Réponse du Chat Islamique',
        text: textToShare,
      }).catch(() => {
        // Fallback: copier dans le presse-papier
        navigator.clipboard.writeText(textToShare);
        alert('Réponse copiée dans le presse-papier !');
      });
    } else {
      navigator.clipboard.writeText(textToShare);
      alert('Réponse copiée dans le presse-papier !');
    }
  };

  // Fonction pour lire à voix haute
  const handlePlayAudio = (content: string, messageId: string) => {
    if (!synthRef.current) {
      alert('La synthèse vocale n\'est pas disponible sur votre navigateur.');
      return;
    }

    // Arrêter la lecture en cours
    if (isPlayingAudio) {
      synthRef.current.cancel();
      setIsPlayingAudio(null);
      return;
    }

    // Nettoyer le texte (enlever les émojis et markdown)
    const cleanText = content
      .replace(/\*\*/g, '')
      .replace(/📖|📜|🤲|🕌|🌅|☀️|🌤️|🌇|🌙|📍|📚|📂|💡/g, '')
      .replace(/\n/g, '. ')
      .substring(0, 500);

    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.lang = 'fr-FR';
    utterance.rate = 0.9;
    utterance.pitch = 1;

    utterance.onend = () => {
      setIsPlayingAudio(null);
    };

    utterance.onerror = () => {
      setIsPlayingAudio(null);
    };

    synthRef.current.speak(utterance);
    setIsPlayingAudio(messageId);
  };

  // Obtenir les messages filtrés pour la recherche
  const getFilteredMessages = () => {
    if (!searchQuery) return messages;
    const query = searchQuery.toLowerCase();
    return messages.filter(
      (msg) =>
        msg.content.toLowerCase().includes(query) ||
        (msg.type === 'user' && msg.content.toLowerCase().includes(query))
    );
  };

  // Obtenir les favoris (depuis le state)
  const getFavorites = () => {
    return favorites;
  };

  // Fonction pour ajouter des points et vérifier les badges
  const addPoints = (points: number) => {
    if (!user) return; // Seulement pour les utilisateurs connectés
    
    setUserPoints(prev => {
      const newPoints = prev + points;
      
      // Vérifier les badges
      const newBadges: string[] = [];
      if (newPoints >= 100 && !userBadges.includes('Bronze')) {
        newBadges.push('Bronze');
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
      }
      if (newPoints >= 500 && !userBadges.includes('Argent')) {
        newBadges.push('Argent');
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
      }
      if (newPoints >= 1000 && !userBadges.includes('Or')) {
        newBadges.push('Or');
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
      }
      if (newPoints >= 5000 && !userBadges.includes('Platine')) {
        newBadges.push('Platine');
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
      }
      
      if (newBadges.length > 0) {
        setUserBadges(prev => [...prev, ...newBadges]);
      }
      
      // Sauvegarder dans localStorage
      localStorage.setItem('hanafiya_user_points', newPoints.toString());
      localStorage.setItem('hanafiya_user_badges', JSON.stringify([...userBadges, ...newBadges]));
      
      return newPoints;
    });
  };

  // Charger les points et badges au démarrage
  useEffect(() => {
    if (user) {
      const savedPoints = localStorage.getItem('hanafiya_user_points');
      const savedBadges = localStorage.getItem('hanafiya_user_badges');
      if (savedPoints) {
        setUserPoints(parseInt(savedPoints, 10));
      }
      if (savedBadges) {
        try {
          setUserBadges(JSON.parse(savedBadges));
        } catch (e) {
          console.error('Erreur lors du chargement des badges:', e);
        }
      }
    }
  }, [user]);

  // Obtenir les suggestions contextuelles basées sur le dernier message
  const getContextualSuggestions = (): Array<{ label: string; action: string }> => {
    if (messages.length < 2) return [];
    
    const lastMessage = messages[messages.length - 1];
    if (lastMessage.type !== 'assistant') return [];

    const content = lastMessage.content.toLowerCase();
    const suggestions: Array<{ label: string; action: string }> = [];

    // Suggestions basées sur le contenu (uniquement base de connaissances et données Hanafiya)
    if (content.includes('exposé') || content.includes('exposés')) {
      suggestions.push({ label: '📚 Exposés récents', action: 'Parle-moi des exposés récents' });
      suggestions.push({ label: '📅 Événements', action: 'Quels sont les événements à venir ?' });
    }

    if (content.includes('événement') || content.includes('événements')) {
      suggestions.push({ label: '📅 Événements', action: 'Quels sont les événements à venir ?' });
      suggestions.push({ label: '📚 Exposés récents', action: 'Parle-moi des exposés récents' });
    }

    if (content.includes('daara') || content.includes('daaras')) {
      suggestions.push({ label: '📖 Daaras', action: 'Quels sont les daaras disponibles ?' });
    }

    return suggestions.slice(0, 3); // Maximum 3 suggestions contextuelles
  };

  if (isMinimized) {
    return (
      <div className="islamic-chat-window fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-80 bg-white rounded-t-2xl shadow-2xl border border-green-200 animate-slide-up">
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-2xl">🕌</span>
            <span className="font-semibold">HanafiyaAI</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={onMaximize}
              className="p-1 hover:bg-white/20 rounded transition-colors"
              aria-label="Agrandir"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded transition-colors"
              aria-label="Fermer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }


  // Fonction pour changer la langue
  const handleLanguageChange = (lang: Language) => {
    setCurrentLanguage(lang);
    translationService.setLanguage(lang);
  };

  // Fonction pour activer les notifications
  const handleEnableNotifications = async () => {
    const granted = await chatNotificationService.requestPermission();
    setNotificationsEnabled(granted);
    if (granted) {
      chatNotificationService.startPrayerReminders();
      chatNotificationService.startDailyVerse();
    }
  };

  // Taille du chat selon l'état expanded
  const chatWidth = isExpanded 
    ? 'w-[calc(100vw-2rem)] sm:w-[600px] lg:w-[700px]' 
    : 'w-[calc(100vw-2rem)] sm:w-96';
  const chatHeight = isExpanded 
    ? 'h-[750px] max-h-[calc(100vh-4rem)]' 
    : 'h-[650px] max-h-[calc(100vh-4rem)]';

  return (
    <div className={`islamic-chat-window fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 ${chatWidth} ${chatHeight} bg-white rounded-2xl shadow-2xl border border-green-200 flex flex-col animate-slide-up overflow-hidden transition-all duration-300`}>
      {/* Confettis animation */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-[100]">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-10px',
                backgroundColor: ['#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6'][Math.floor(Math.random() * 5)],
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            />
          ))}
          <style>{`
            @keyframes confetti {
              0% {
                transform: translateY(0) rotate(0deg);
                opacity: 1;
              }
              100% {
                transform: translateY(100vh) rotate(360deg);
                opacity: 0;
              }
            }
            .animate-confetti {
              animation: confetti linear forwards;
            }
          `}</style>
        </div>
      )}
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4 rounded-t-2xl flex items-center justify-between relative overflow-hidden">
        {/* Motifs décoratifs */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16"></div>
          <div className="absolute bottom-0 right-0 w-40 h-40 bg-white rounded-full translate-x-20 translate-y-20"></div>
        </div>
        
        <div className="flex items-center space-x-2 relative z-10">
          <span className="text-2xl">🕌</span>
          <div>
            <h3 className="font-bold text-lg">HanafiyaAI</h3>
            <div className="flex items-center gap-2">
              <p className="text-xs opacity-90">Assalamu Alaikum</p>
              {user && userPoints > 0 && (
                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full font-semibold">
                  ⭐ {userPoints} pts
                </span>
              )}
              {user && userBadges.length > 0 && (
                <div className="flex gap-1">
                  {userBadges.map((badge, idx) => (
                    <span key={idx} className="text-xs" title={badge}>
                      {badge === 'Bronze' ? '🥉' : badge === 'Argent' ? '🥈' : badge === 'Or' ? '🥇' : badge === 'Platine' ? '💎' : '🏆'}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-1 relative z-10">
          {/* Bouton agrandir/réduire */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 hover:bg-white/20 rounded transition-colors"
            title={isExpanded ? "Réduire" : "Agrandir"}
            aria-label={isExpanded ? "Réduire" : "Agrandir"}
          >
            {isExpanded ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            )}
          </button>

          {/* Sélecteur de langue */}
          <div className="relative">
            <button
              onClick={() => {
                const langs: Language[] = ['fr', 'ar', 'en'];
                const currentIndex = langs.indexOf(currentLanguage);
                const nextLang = langs[(currentIndex + 1) % langs.length];
                handleLanguageChange(nextLang);
              }}
              className="p-1.5 hover:bg-white/20 rounded transition-colors"
              title={`Langue: ${translationService.getLanguageName(currentLanguage)}`}
            >
              <span className="text-lg">{translationService.getLanguageIcon(currentLanguage)}</span>
            </button>
          </div>

          {/* Notifications */}
          <button
            onClick={handleEnableNotifications}
            className={`p-1.5 hover:bg-white/20 rounded transition-colors ${notificationsEnabled ? 'text-yellow-300' : ''}`}
            title={notificationsEnabled ? "Notifications activées" : "Activer les notifications"}
          >
            <svg className="w-5 h-5" fill={notificationsEnabled ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </button>

          <button
            onClick={clearHistory}
            className="p-1.5 hover:bg-white/20 rounded transition-colors"
            title="Effacer l'historique"
            aria-label="Effacer l'historique"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          <button
            onClick={onMinimize}
            className="p-1.5 hover:bg-white/20 rounded transition-colors"
            aria-label="Réduire"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded transition-colors"
            aria-label="Fermer"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-green-200 bg-white">
        <div className="flex overflow-x-auto">
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex-1 px-3 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'chat'
                ? 'text-green-700 border-b-2 border-green-700 bg-green-50'
                : 'text-gray-600 hover:text-green-700 hover:bg-green-50/50'
            }`}
          >
            💬 Chat
          </button>
          {user && (
            <>
              <button
                onClick={() => setActiveTab('favorites')}
                className={`flex-1 px-3 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'favorites'
                    ? 'text-green-700 border-b-2 border-green-700 bg-green-50'
                    : 'text-gray-600 hover:text-green-700 hover:bg-green-50/50'
                }`}
              >
                ⭐ Favoris ({getFavorites().length})
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`flex-1 px-3 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'history'
                    ? 'text-green-700 border-b-2 border-green-700 bg-green-50'
                    : 'text-gray-600 hover:text-green-700 hover:bg-green-50/50'
                }`}
              >
                📜 Historique
              </button>
              <button
                onClick={() => setActiveTab('stats')}
                className={`flex-1 px-3 py-3 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'stats'
                    ? 'text-green-700 border-b-2 border-green-700 bg-green-50'
                    : 'text-gray-600 hover:text-green-700 hover:bg-green-50/50'
                }`}
              >
                📊 Stats
              </button>
            </>
          )}
        </div>
      </div>

      {/* Contenu des onglets */}
      <div className="flex-1 overflow-y-auto bg-gradient-to-b from-green-50/30 to-white relative">
        {activeTab === 'chat' && (
          <>
            {/* Indicateur de requêtes restantes (non connectés) */}
            {!user && remainingRequests < Infinity && (
              <div className="p-3 bg-yellow-50 border-b border-yellow-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-yellow-800 font-medium">
                    📊 {remainingRequests} requête{remainingRequests > 1 ? 's' : ''} restante{remainingRequests > 1 ? 's' : ''} aujourd'hui
                  </span>
                  <button
                    onClick={() => navigate('/login')}
                    className="text-green-700 hover:text-green-800 font-semibold underline"
                  >
                    Se connecter pour plus
                  </button>
                </div>
              </div>
            )}

            {/* Recherche dans le chat */}
            {messages.length > 2 && (
              <div className="p-3 bg-white border-b border-green-100">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="🔍 Rechercher dans la conversation..."
                  className="w-full px-3 py-2 text-sm border border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Message de bienvenue (affiché seulement s'il n'y a pas de messages utilisateur) */}
            {messages.length === 1 && messages[0].id === 'welcome' && !searchQuery && (
              <div className="flex-1 flex items-center justify-center p-8 pb-24">
                <div className="relative w-full max-w-2xl">
                  <div className="text-2xl md:text-3xl font-semibold text-green-700 text-center">
                    {displayedWelcomeText}
                    {isTypingWelcome && (
                      <span className="inline-block w-0.5 h-6 md:h-8 bg-green-700 ml-1 animate-pulse">|</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="p-4 pb-24">
              {getFilteredMessages()
                .filter(msg => msg.id !== 'welcome') // Exclure le message de bienvenue des messages normaux
                .map((message, index) => {
                  const filteredMessages = getFilteredMessages().filter(msg => msg.id !== 'welcome');
                  const isLastAssistantMessage = 
                    message.type === 'assistant' && 
                    index === filteredMessages.length - 1 &&
                    !isLoading &&
                    !isTypingResponse[message.id] &&
                    lastUserQuestion !== '';
                  const isCurrentlyTyping = isTypingResponse[message.id] || false;
                  
                  return (
                    <React.Fragment key={message.id}>
                      <MessageBubble
                        message={message}
                        onShare={handleShare}
                        onPlayAudio={handlePlayAudio}
                        isPlayingAudio={isPlayingAudio === message.id}
                        isAuthenticated={!!user}
                        onFavoriteChange={loadFavorites}
                        isTyping={isCurrentlyTyping}
                      />
                      {/* Questions similaires après la dernière réponse de l'assistant */}
                      {isLastAssistantMessage && (
                        <SuggestedQuestions
                          query={lastUserQuestion}
                          onQuestionClick={(question) => {
                            setLastUserQuestion(''); // Réinitialiser pour éviter les doublons
                            handleSendMessage(question);
                          }}
                          limit={3}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              {isLoading && (
                <div className="flex justify-start mb-4">
                  <div className="bg-white rounded-2xl px-4 py-3 shadow-lg border border-green-100">
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-green-600 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-green-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-2 h-2 bg-green-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Suggestions contextuelles */}
            {getContextualSuggestions().length > 0 && !isLoading && (
              <div className="px-4 pb-2">
                <div className="flex flex-wrap gap-2">
                  {getContextualSuggestions().map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickAction(suggestion.action)}
                      className="px-3 py-1.5 text-xs bg-green-100 border border-green-300 rounded-full hover:bg-green-200 hover:border-green-400 transition-colors text-green-800 font-medium"
                    >
                      {suggestion.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Suggestions rapides fixes en bas */}
            {messages.length === 1 && messages[0].id === 'welcome' && !searchQuery && (
              <div className="sticky bottom-0 left-0 right-0 bg-gradient-to-br from-green-50 via-emerald-50 to-green-50 border-t border-green-100 px-4 py-3 z-10 shadow-lg">
                <p className="text-xs text-gray-600 mb-2 font-medium">💡 Suggestions rapides :</p>
                <div className="flex flex-wrap gap-2">
                  {quickSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickAction(suggestion.action)}
                      className="px-3 py-2 text-xs sm:text-sm bg-gradient-to-r from-white to-green-50 border border-green-200 rounded-full hover:from-green-100 hover:to-emerald-100 hover:border-green-400 hover:shadow-sm transition-all duration-200 text-gray-700 font-medium transform hover:scale-105 active:scale-95 whitespace-normal break-words max-w-full"
                    >
                      {suggestion.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'favorites' && user && (
          <div className="p-4">
            {loadingFavorites ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Chargement des favoris...</p>
              </div>
            ) : getFavorites().length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">⭐</div>
                <p className="text-gray-600">Aucun favori pour le moment</p>
                <p className="text-sm text-gray-500 mt-2">Ajoutez des réponses aux favoris en cliquant sur l'étoile ⭐</p>
              </div>
            ) : (
              <div className="space-y-4">
                {getFavorites().map((favorite) => (
                  <div
                    key={favorite.id}
                    className="bg-white rounded-xl p-4 border border-green-100 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-semibold">
                            {favorite.type === 'verse' ? '📖' : favorite.type === 'hadith' ? '📜' : favorite.type === 'dua' ? '🤲' : '🕌'}
                          </span>
                          <span className="font-bold text-gray-900">{favorite.title}</span>
                        </div>
                        <p className="text-gray-600 text-sm whitespace-pre-wrap line-clamp-3">
                          {favorite.content.substring(0, 200)}...
                        </p>
                        {favorite.reference && (
                          <p className="text-xs text-gray-500 mt-2">📚 {favorite.reference}</p>
                        )}
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await favoritesApiService.removeFavorite(favorite.id);
                            await loadFavorites(); // Recharger les favoris
                          } catch (error) {
                            console.error('Erreur lors de la suppression:', error);
                          }
                        }}
                        className="ml-2 text-red-500 hover:text-red-700"
                        title="Retirer des favoris"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex items-center space-x-2 mt-3 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => handleShare(favorite.content)}
                        className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                      >
                        Partager
                      </button>
                      <button
                        onClick={() => handlePlayAudio(favorite.content, favorite.id)}
                        className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                      >
                        Audio
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && user && (
          <div className="p-4">
            <div className="mb-4">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="🔍 Rechercher dans l'historique..."
                className="w-full px-4 py-2 border border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            {getFilteredMessages().filter(m => m.type === 'user' || m.type === 'assistant').length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📜</div>
                <p className="text-gray-600">Aucun historique</p>
              </div>
            ) : (
              <div className="space-y-4">
                {getFilteredMessages()
                  .filter(m => m.type === 'user' || m.type === 'assistant')
                  .map((message) => (
                    <div
                      key={message.id}
                      className={`bg-white rounded-xl p-4 border ${
                        message.type === 'user' ? 'border-green-200' : 'border-blue-200'
                      } shadow-sm`}
                    >
                      <div className="flex items-start space-x-3">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            message.type === 'user'
                              ? 'bg-green-600 text-white'
                              : 'bg-blue-600 text-white'
                          }`}
                        >
                          {message.type === 'user' ? '👤' : '🤖'}
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-600 text-sm whitespace-pre-wrap">
                            {message.content.substring(0, 300)}
                            {message.content.length > 300 ? '...' : ''}
                          </p>
                          <p className="text-xs text-gray-400 mt-2">
                            {message.timestamp.toLocaleString('fr-FR')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'stats' && user && (
          <div className="p-4">
            {statistics ? (
              <div className="space-y-6">
                {/* Statistiques générales */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                    <div className="text-3xl font-bold text-green-700">{statistics.totalQuestions}</div>
                    <div className="text-sm text-green-600 mt-1">Questions posées</div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-200">
                    <div className="text-3xl font-bold text-blue-700">{statistics.totalFavorites}</div>
                    <div className="text-sm text-blue-600 mt-1">Favoris sauvegardés</div>
                  </div>
                </div>

                {/* Graphique des questions par jour */}
                <div className="bg-white rounded-xl p-4 border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-4">📈 Activité (7 derniers jours)</h3>
                  <div className="flex items-end justify-between h-32 space-x-2">
                    {statistics.questionsByDay.map((day, index) => {
                      const maxCount = Math.max(...statistics.questionsByDay.map(d => d.count), 1);
                      const height = (day.count / maxCount) * 100;
                      return (
                        <div key={index} className="flex-1 flex flex-col items-center">
                          <div className="w-full bg-green-100 rounded-t-lg relative" style={{ height: `${height}%`, minHeight: '4px' }}>
                            {day.count > 0 && (
                              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs font-semibold text-green-700">
                                {day.count}
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-gray-600 mt-2 text-center">{day.date}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Catégories favorites */}
                {statistics.favoriteCategories.length > 0 && (
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <h3 className="font-bold text-gray-900 mb-4">⭐ Catégories favorites</h3>
                    <div className="space-y-2">
                      {statistics.favoriteCategories.map((cat, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span className="text-sm text-gray-700 capitalize">{cat.category}</span>
                          <div className="flex items-center space-x-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-green-600 h-2 rounded-full"
                                style={{ width: `${(cat.count / statistics.totalFavorites) * 100}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-semibold text-green-700 w-8 text-right">{cat.count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fonctionnalités les plus utilisées */}
                {statistics.mostUsedFeatures.length > 0 && (
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <h3 className="font-bold text-gray-900 mb-4">🎯 Fonctionnalités les plus utilisées</h3>
                    <div className="space-y-2">
                      {statistics.mostUsedFeatures.map((feature, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span className="text-sm text-gray-700 capitalize">{feature.feature}</span>
                          <span className="text-sm font-semibold text-blue-700">{feature.count} fois</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Temps de réponse moyen */}
                {statistics.averageResponseTime > 0 && (
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200">
                    <div className="text-2xl font-bold text-purple-700">{statistics.averageResponseTime}ms</div>
                    <div className="text-sm text-purple-600 mt-1">Temps de réponse moyen</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📊</div>
                <p className="text-gray-600">Chargement des statistiques...</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Input - Visible seulement sur l'onglet chat */}
      {activeTab === 'chat' && (
        <div className="p-4 bg-white border-t border-green-100">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowQuestionsBrowser(true)}
              className="p-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full hover:from-blue-600 hover:to-blue-700 transition-all transform hover:scale-110"
              title="Parcourir toutes les questions"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Posez votre question..."
              className="flex-1 px-4 py-2 border border-green-200 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              disabled={isLoading || (!user && remainingRequests === 0)}
            />
            <button
              onClick={() => {
                handleSendMessage();
                // Animation de pulsation
                setShowConfetti(false);
              }}
              disabled={!inputValue.trim() || isLoading || (!user && remainingRequests === 0)}
              className={`p-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-full hover:from-green-700 hover:to-green-800 transition-all transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
                inputValue.trim() && !isLoading ? 'animate-pulse' : ''
              }`}
              aria-label="Envoyer"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-gray-500">
              {!user && remainingRequests < Infinity && (
                <span className={`font-semibold ${remainingRequests === 0 ? 'text-red-600' : 'text-yellow-600'}`}>
                  {remainingRequests === 0 ? '🔒 Limite atteinte' : `${remainingRequests} requête${remainingRequests > 1 ? 's' : ''} restante${remainingRequests > 1 ? 's' : ''}`}
                </span>
              )}
              {user && <span>Appuyez sur Entrée pour envoyer</span>}
            </p>
            {!user && (
              <button
                onClick={() => navigate('/login')}
                className="text-xs text-green-700 hover:text-green-800 font-semibold underline"
              >
                Se connecter
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modal de limitation */}
      {showLimitModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
          onClick={() => setShowLimitModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">🔒</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Limite de requêtes atteinte
              </h3>
              <p className="text-gray-600 mb-4">
                Vous avez utilisé vos <span className="font-bold text-green-600">5 requêtes gratuites</span> aujourd'hui.
              </p>
              <p className="text-sm text-gray-500">
                Connectez-vous pour continuer à utiliser le chat sans limite ! 🚀
              </p>
            </div>

            {/* Avantages de se connecter */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 mb-6 border border-green-200">
              <h4 className="font-bold text-green-900 mb-4 text-lg flex items-center justify-center">
                <span className="mr-2">✨</span>
                Avantages de se connecter
              </h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3 bg-white/60 rounded-lg p-3">
                  <span className="text-2xl">♾️</span>
                  <div>
                    <p className="font-semibold text-green-900">Requêtes illimitées</p>
                    <p className="text-sm text-green-700">Posez autant de questions que vous voulez, sans limite quotidienne</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 bg-white/60 rounded-lg p-3">
                  <span className="text-2xl">⭐</span>
                  <div>
                    <p className="font-semibold text-green-900">Sauvegarder vos favoris</p>
                    <p className="text-sm text-green-700">Enregistrez vos versets, hadiths et douas préférés pour y accéder facilement</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 bg-white/60 rounded-lg p-3">
                  <span className="text-2xl">📜</span>
                  <div>
                    <p className="font-semibold text-green-900">Historique complet</p>
                    <p className="text-sm text-green-700">Consultez toutes vos conversations précédentes et recherchez dedans</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 bg-white/60 rounded-lg p-3">
                  <span className="text-2xl">🎯</span>
                  <div>
                    <p className="font-semibold text-green-900">Fonctionnalités avancées</p>
                    <p className="text-sm text-green-700">Accédez à toutes les fonctionnalités : partage, audio, recherche, etc.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowLimitModal(false);
                  navigate('/login');
                }}
                className="w-full px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all transform hover:scale-[1.02] font-semibold text-lg shadow-lg"
              >
                🔐 Se connecter maintenant
              </button>
              <button
                onClick={() => {
                  setShowLimitModal(false);
                  navigate('/register');
                }}
                className="w-full px-4 py-2 border-2 border-green-600 text-green-700 rounded-lg hover:bg-green-50 transition-colors font-medium"
              >
                Créer un compte gratuit
              </button>
              <button
                onClick={() => setShowLimitModal(false)}
                className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
              >
                Peut-être plus tard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigateur de questions */}
      {showQuestionsBrowser && (
        <QuestionsBrowser
          onQuestionClick={(question) => {
            setLastUserQuestion(''); // Réinitialiser pour éviter les doublons
            handleSendMessage(question);
          }}
          onClose={() => setShowQuestionsBrowser(false)}
        />
      )}
    </div>
  );
};

